import React, { useCallback, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Calculator,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Info,
  Lightbulb,
  Receipt,
  Shield,
  Send,
  Target,
  TrendingUp,
  Users,
  X
} from 'lucide-react';
import { buildAiAdviceCards, buildAiAdvisorStats, buildAiQuestionAnswer } from '../../lib/aiAdvisor';

const DISMISSED_STORAGE_KEY = '70lives_ai_assist_dismissed_advice';
const HIDDEN_QUESTIONS_STORAGE_KEY = '70lives_ai_assist_hidden_questions';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'tax_vat', label: 'Tax & VAT' },
  { id: 'budget_pricing', label: 'Budget & Pricing' },
  { id: 'bookkeeping', label: 'Bookkeeping' },
  { id: 'payroll', label: 'Payroll' }
];

const CATEGORY_LABELS = {
  setup: 'Setup',
  vat: 'VAT',
  tax: 'Tax',
  budget: 'Budget',
  pricing: 'Pricing',
  bookkeeping: 'Bookkeeping',
  payroll: 'Payroll',
  support: 'Support'
};

const CATEGORY_FILTERS = {
  tax_vat: new Set(['tax', 'vat']),
  budget_pricing: new Set(['budget', 'pricing']),
  bookkeeping: new Set(['bookkeeping', 'setup', 'support']),
  payroll: new Set(['payroll'])
};

const getStoredDismissedIds = () => {
  if (typeof window === 'undefined') return [];

  try {
    const saved = window.localStorage.getItem(DISMISSED_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getStoredHiddenQuestionKeys = () => {
  if (typeof window === 'undefined') return [];

  try {
    const saved = window.localStorage.getItem(HIDDEN_QUESTIONS_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const storeDismissedIds = (ids) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(ids));
  } catch {}
};

const storeHiddenQuestionKeys = (ids) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(HIDDEN_QUESTIONS_STORAGE_KEY, JSON.stringify(ids));
  } catch {}
};

const normalizeQuestionKey = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getCardIcon = (category) => {
  switch (category) {
    case 'vat':
      return Receipt;
    case 'tax':
      return Calculator;
    case 'budget':
      return Target;
    case 'pricing':
      return TrendingUp;
    case 'payroll':
      return Users;
    case 'setup':
      return Shield;
    default:
      return Lightbulb;
  }
};

const getSeverityClasses = (severity) => {
  if (severity === 'warning') {
    return 'border-amber-200 bg-amber-50 text-amber-900';
  }

  return 'border-gray-200 bg-white text-gray-900';
};

const getBadgeClasses = (severity) => {
  if (severity === 'warning') {
    return 'bg-amber-100 text-amber-800 border-amber-200';
  }

  return 'bg-gray-100 text-gray-700 border-gray-200';
};

const getBusinessTypeLabel = (businessType) => (
  businessType === 'limited_company' ? 'Limited Company' : 'Sole Trader'
);

const QUESTION_SUGGESTIONS = [
  { label: 'VAT on food', question: 'Can I reclaim VAT on food?' },
  { label: 'Beauty VAT rate', question: 'What VAT rate should I use for beauty services?' },
  { label: 'VAT threshold', question: 'When do I need to register for VAT?' },
  { label: 'Work clothing', question: 'Can I claim work clothes or uniforms?' },
  { label: 'Retail stock', question: 'Should I separate retail products from services?' },
  { label: 'Gift cards', question: 'How should I track gift cards or deposits?' },
  { label: 'Employer PRSI', question: 'What employer PRSI should I use?' },
  { label: 'Staff pricing', question: 'How should I price a service with staff?' },
  { label: "Director's Loan", question: "When should I use Director's Loan?" }
];

const AIHelper = ({
  transactions = [],
  targetIncomeData = {},
  formatCurrency,
  currentPage = 'dashboard',
  currentCountry = {},
  businessType = 'sole_trader',
  vatSettings = {},
  pricingLaborHourlyCost = null,
  referenceDate = null
}) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [showDismissed, setShowDismissed] = useState(false);
  const [dismissedIds, setDismissedIds] = useState(getStoredDismissedIds);
  const [, setHiddenQuestionKeys] = useState(getStoredHiddenQuestionKeys);
  const [expandedCardIds, setExpandedCardIds] = useState(() => new Set());
  const [questionInput, setQuestionInput] = useState('');
  const [questionHistory, setQuestionHistory] = useState([]);
  const effectiveReferenceDate = useMemo(() => referenceDate || new Date(), [referenceDate]);

  const formatMoney = useCallback((value) => {
    const amount = Number(value) || 0;
    if (typeof formatCurrency === 'function') {
      return formatCurrency(amount);
    }

    try {
      return new Intl.NumberFormat('en-IE', {
        style: 'currency',
        currency: currentCountry?.currency || 'EUR'
      }).format(amount);
    } catch {
      return amount.toFixed(2);
    }
  }, [currentCountry?.currency, formatCurrency]);

  const stats = useMemo(() => buildAiAdvisorStats({
    transactions,
    targetIncomeData,
    currentCountry,
    businessType,
    vatSettings,
    pricingLaborHourlyCost,
    referenceDate: effectiveReferenceDate
  }), [
    transactions,
    targetIncomeData,
    currentCountry,
    businessType,
    vatSettings,
    pricingLaborHourlyCost,
    effectiveReferenceDate
  ]);

  const adviceCards = useMemo(() => buildAiAdviceCards({
    ...stats,
    referenceDate: effectiveReferenceDate
  }), [stats, effectiveReferenceDate]);

  const warningCount = adviceCards.filter((card) => card.severity === 'warning').length;
  const dismissedSet = useMemo(() => new Set(dismissedIds), [dismissedIds]);

  const filteredCards = adviceCards.filter((card) => {
    if (!showDismissed && dismissedSet.has(card.id)) return false;
    if (activeFilter === 'all') return true;
    return CATEGORY_FILTERS[activeFilter]?.has(card.category);
  });

  const topCards = filteredCards.slice(0, 3);

  const dismissAdvice = (id) => {
    setDismissedIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      storeDismissedIds(next);
      return next;
    });
  };

  const resetDismissedAdvice = () => {
    setDismissedIds([]);
    storeDismissedIds([]);
  };

  const markAdviceUnderstood = (id) => {
    dismissAdvice(id);
    setExpandedCardIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      next.delete(`top-${id}`);
      return next;
    });
  };

  const toggleCardExpanded = (id) => {
    setExpandedCardIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const askQuestion = (questionText = questionInput) => {
    const trimmed = String(questionText || '').trim();
    if (!trimmed) return;
    const questionKey = normalizeQuestionKey(trimmed);

    setQuestionHistory((prev) => {
      const existingIndex = prev.findIndex((entry) => entry.questionKey === questionKey);
      if (existingIndex >= 0) {
        return prev.filter((entry) => entry.questionKey !== questionKey);
      }

      const answer = buildAiQuestionAnswer({
        question: trimmed,
        stats,
        adviceCards,
        currentCountry,
        referenceDate: effectiveReferenceDate
      });

      return [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          questionKey,
          question: trimmed,
          ...answer
        },
        ...prev
      ].slice(0, 6);
    });
    setHiddenQuestionKeys((prev) => {
      if (!prev.includes(questionKey)) return prev;
      const next = prev.filter((key) => key !== questionKey);
      storeHiddenQuestionKeys(next);
      return next;
    });
    setQuestionInput('');
  };

  const hideQuestionAnswer = (entry) => {
    setQuestionHistory((prev) => prev.filter((item) => item.id !== entry.id));
    setHiddenQuestionKeys((prev) => {
      if (prev.includes(entry.questionKey)) return prev;
      const next = [...prev, entry.questionKey];
      storeHiddenQuestionKeys(next);
      return next;
    });
  };

  const renderCardBody = (card) => (
    <>
      <p className="mt-3 text-sm leading-relaxed opacity-90">{card.summary}</p>
      {card.detail && (
        <p className="mt-2 text-sm leading-relaxed opacity-80">{card.detail}</p>
      )}

      {card.actions?.length > 0 && (
        <div className="mt-3 space-y-2">
          {card.actions.map((action) => (
            <div key={action} className="flex items-start gap-2 text-sm">
              <CheckCircle size={14} className="mt-0.5 flex-shrink-0 text-green-600" />
              <span>{action}</span>
            </div>
          ))}
        </div>
      )}

      {card.sources?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-black/5 pt-3">
          {card.sources.map((source) => (
            <a
              key={source.url}
              href={source.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-1 text-[11px] font-semibold text-gray-600 hover:bg-gray-50"
            >
              {source.label}
              <ExternalLink size={12} />
            </a>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2 border-t border-black/5 pt-3">
        <button
          type="button"
          onClick={() => markAdviceUnderstood(card.id)}
          className="rounded-lg bg-warn-gradient px-3 py-1.5 text-xs font-semibold text-[color:var(--color-text)] hover:brightness-95"
        >
          Got it
        </button>
        <button
          type="button"
          onClick={() => markAdviceUnderstood(card.id)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
        >
          Hide
        </button>
      </div>
    </>
  );

  const hasBudgetFocus = currentPage === 'budget' || activeFilter === 'budget_pricing';
  const activeBusinessLabel = getBusinessTypeLabel(stats.businessType);
  const vatLabel = stats.taxSystem === 'sales'
    ? 'Sales tax'
    : (stats.vatRegistered ? 'VAT registered' : 'VAT not registered');

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="bg-accent-gradient p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/60 bg-white/60 px-3 py-1 text-xs font-semibold text-[color:var(--color-text)]">
                <Lightbulb size={14} />
                70Lives AI Assist
              </div>
              <h3 className="mt-3 text-2xl font-semibold text-[color:var(--color-text)]">
                Bookkeeping, tax, VAT, and pricing guidance
              </h3>
              <p className="mt-2 max-w-3xl text-sm text-[color:var(--color-text)]/80">
                The assistant reads the business setup, VAT status, budget plan, labour profiles, and transaction categories to show practical notes before reports or prices become misleading.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4 lg:min-w-[420px]">
              <div className="rounded-lg bg-white/80 p-3">
                <div className="text-gray-500">Country</div>
                <div className="mt-1 font-semibold text-gray-900">{stats.countryName}</div>
              </div>
              <div className="rounded-lg bg-white/80 p-3">
                <div className="text-gray-500">Business</div>
                <div className="mt-1 font-semibold text-gray-900">{activeBusinessLabel}</div>
              </div>
              <div className="rounded-lg bg-white/80 p-3">
                <div className="text-gray-500">VAT status</div>
                <div className="mt-1 font-semibold text-gray-900">{vatLabel}</div>
              </div>
              <div className="rounded-lg bg-white/80 p-3">
                <div className="text-gray-500">Warnings</div>
                <div className="mt-1 font-semibold text-gray-900">{warningCount}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 border-t border-gray-200 md:grid-cols-4">
          <div className="border-b border-gray-200 p-4 md:border-b-0 md:border-r">
            <div className="text-xs uppercase tracking-wide text-gray-500">Transactions</div>
            <div className="mt-1 text-xl font-semibold text-gray-900">{stats.totalTransactions}</div>
          </div>
          <div className="border-b border-gray-200 p-4 md:border-b-0 md:border-r">
            <div className="text-xs uppercase tracking-wide text-gray-500">This month profit</div>
            <div className="mt-1 text-xl font-semibold text-gray-900">{formatMoney(stats.currentMonthProfit)}</div>
          </div>
          <div className="border-b border-gray-200 p-4 md:border-b-0 md:border-r">
            <div className="text-xs uppercase tracking-wide text-gray-500">Labour floor</div>
            <div className="mt-1 text-xl font-semibold text-gray-900">
              {stats.labourHourlyFromBudget > 0 ? `${formatMoney(stats.labourHourlyFromBudget)}/hr` : 'Not set'}
            </div>
          </div>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-gray-500">Open hours</div>
            <div className="mt-1 text-xl font-semibold text-gray-900">
              {stats.weeklyOpenHours > 0 ? `${stats.weeklyOpenHours}/week` : 'Not set'}
            </div>
          </div>
        </div>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 p-4">
          <h4 className="text-base font-semibold text-gray-900">Ask AI</h4>
          <p className="mt-1 text-xs text-gray-600">
            Ask about VAT, expenses, payroll, budget, or pricing.
          </p>
        </div>
        <div className="p-4">
          <div className="mx-auto max-w-5xl space-y-3">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                askQuestion();
              }}
              className="flex flex-col gap-2 sm:flex-row"
            >
              <input
                type="text"
                value={questionInput}
                onChange={(event) => setQuestionInput(event.target.value)}
                placeholder="Ask about VAT, expenses, payroll, pricing..."
                className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[color:var(--color-primary)] focus:outline-none"
              />
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-warn-gradient px-4 py-2 text-sm font-semibold text-[color:var(--color-text)] hover:brightness-95"
              >
                <Send size={16} />
                Ask
              </button>
            </form>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              {QUESTION_SUGGESTIONS.map((suggestion) => (
                (() => {
                  const suggestionKey = normalizeQuestionKey(suggestion.question);
                  const isOpen = questionHistory.some((entry) => entry.questionKey === suggestionKey);
                  return (
                    <button
                      key={suggestion.question}
                      type="button"
                      onClick={() => askQuestion(suggestion.question)}
                      className={`min-h-[38px] rounded-lg border px-3 py-2 text-center text-xs font-semibold leading-tight hover:bg-white ${
                        isOpen
                          ? 'border-gray-900 bg-white text-gray-900'
                          : 'border-gray-200 bg-gray-50 text-gray-700'
                      }`}
                      aria-pressed={isOpen}
                    >
                      {suggestion.label}
                    </button>
                  );
                })()
              ))}
            </div>

            {questionHistory.length > 0 && (
              <div className="space-y-3">
                {questionHistory.map((entry) => (
                  <article key={entry.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">You asked</div>
                        <div className="mt-1 text-sm font-semibold text-gray-900">{entry.question}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => hideQuestionAnswer(entry)}
                        className="rounded-md p-1 text-gray-400 hover:bg-white hover:text-gray-700"
                        aria-label={`Hide answer for ${entry.question}`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className="mt-3 rounded-lg bg-white p-3">
                      <div className="text-sm font-semibold text-gray-900">{entry.title}</div>
                      <p className="mt-1 text-sm leading-relaxed text-gray-700">{entry.answer}</p>
                      {entry.actions?.length > 0 && (
                        <div className="mt-3 space-y-1.5">
                          {entry.actions.map((action) => (
                            <div key={action} className="flex items-start gap-2 text-sm text-gray-700">
                              <CheckCircle size={14} className="mt-0.5 flex-shrink-0 text-green-600" />
                              <span>{action}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
                        <button
                          type="button"
                          onClick={() => hideQuestionAnswer(entry)}
                          className="rounded-lg bg-warn-gradient px-3 py-1.5 text-xs font-semibold text-[color:var(--color-text)] hover:brightness-95"
                        >
                          Got it
                        </button>
                        <button
                          type="button"
                          onClick={() => hideQuestionAnswer(entry)}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                        >
                          Hide
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {topCards.length > 0 && (
        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h4 className="text-base font-semibold text-gray-900">Next Best Notes</h4>
              <p className="text-xs text-gray-600">
                These are the highest-priority items for the current ledger setup.
              </p>
            </div>
            {hasBudgetFocus && (
              <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800">
                Budget focus
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            {topCards.map((card) => {
              const Icon = card.severity === 'warning' ? AlertTriangle : getCardIcon(card.category);
              const isExpanded = expandedCardIds.has(`top-${card.id}`);
              return (
                <article key={`top-${card.id}`} className={`rounded-lg border p-4 ${getSeverityClasses(card.severity)}`}>
                  <button
                    type="button"
                    onClick={() => toggleCardExpanded(`top-${card.id}`)}
                    className="flex w-full items-start gap-3 text-left"
                    aria-expanded={isExpanded}
                  >
                    <Icon size={18} className="mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold">{card.title}</div>
                      <div className="mt-1 text-xs opacity-70">{isExpanded ? 'Tap to close' : 'Tap to read'}</div>
                    </div>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {isExpanded && renderCardBody(card)}
                </article>
              );
            })}
          </div>
        </section>
      )}

      <section className="rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h4 className="text-base font-semibold text-gray-900">Advice Library</h4>
            <p className="text-xs text-gray-600">
              Hide notes after reading them; show hidden notes again when reviewing a return or price model.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                onClick={() => setActiveFilter(filter.id)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                  activeFilter === filter.id
                    ? 'border-[color:var(--color-primary)] bg-info-gradient text-[color:var(--color-text)]'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {filter.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShowDismissed((prev) => !prev)}
              className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
            >
              {showDismissed ? 'Hide read' : 'Show read'}
            </button>
          </div>
        </div>

        {filteredCards.length === 0 ? (
          <div className="p-6 text-sm text-gray-600">
            No visible notes in this filter.
            {dismissedIds.length > 0 && (
              <button
                type="button"
                onClick={resetDismissedAdvice}
                className="ml-2 font-semibold text-[color:var(--color-text)] underline"
              >
                Restore hidden notes
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 p-4 xl:grid-cols-2">
            {filteredCards.map((card) => {
              const Icon = card.severity === 'warning' ? AlertTriangle : getCardIcon(card.category);
              const isDismissed = dismissedSet.has(card.id);
              const isExpanded = expandedCardIds.has(card.id);

              return (
                <article
                  key={card.id}
                  className={`rounded-lg border p-4 shadow-sm ${getSeverityClasses(card.severity)} ${isDismissed ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => toggleCardExpanded(card.id)}
                      className="flex min-w-0 flex-1 items-start gap-3 text-left"
                      aria-expanded={isExpanded}
                    >
                      <div className="mt-0.5 rounded-lg bg-white/70 p-2">
                        <Icon size={18} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getBadgeClasses(card.severity)}`}>
                            {CATEGORY_LABELS[card.category] || 'Advice'}
                          </span>
                          {card.severity === 'warning' && (
                            <span className="rounded-full border border-amber-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                              Review
                            </span>
                          )}
                          {isDismissed && (
                            <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-semibold text-gray-500">
                              Read
                            </span>
                          )}
                        </div>
                        <h5 className="mt-2 text-sm font-semibold">{card.title}</h5>
                        <div className="mt-1 text-xs opacity-70">{isExpanded ? 'Tap to close' : 'Tap to read'}</div>
                      </div>
                      <span className="mt-1 flex-shrink-0 text-gray-500">
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    </button>
                    {!isDismissed && (
                      <button
                        type="button"
                        onClick={() => dismissAdvice(card.id)}
                        className="rounded-md p-1 text-gray-400 hover:bg-white hover:text-gray-700"
                        aria-label={`Hide ${card.title}`}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>

                  {isExpanded && renderCardBody(card)}
                </article>
              );
            })}
          </div>
        )}
      </section>

      <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
        <div className="flex items-start gap-2">
          <Info size={18} className="mt-0.5 flex-shrink-0" />
          <p>
            AI Assist gives bookkeeping prompts and calculation guidance. It does not file returns or replace professional advice, especially for VAT reclaim, payroll class, director pay, mixed-use expenses, or corporation tax treatment.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIHelper;
